// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD STATS - VUE FERMIER ÉPURÉE (Overview)
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/DashboardStats.jsx
// Version simplifiée : KPIs + Alertes Stock + Prochains Retraits + Actions
// Farm-Booking - Papy 73+ - Janvier 2026
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
  Package, CheckCircle2, Leaf, TrendingUp, Download, Calendar, 
  RefreshCw, AlertTriangle, FileSpreadsheet, FileText, BarChart3
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const DashboardStats = ({ dashboardData }) => {
  const [alertesStock, setAlertesStock] = useState([]);
  const [prochainsRetraits, setProchainsRetraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = async () => {
    try {
      setRefreshing(true);
      
      // Alertes Stock
      const stockRes = await fetch(`${API_BASE}/produits`);
      if (stockRes.ok) {
        const stockData = await stockRes.json();
        const produits = stockData.data || stockData || [];
        const alertes = produits
          .filter(p => p.actif === 'oui')
          .map(p => {
            const stock = parseFloat(p.stock_actuel) || parseFloat(p.quantite_disponible) || 0;
            const reserve = parseFloat(p.quantite_reservee) || 0;
            const seuil = parseFloat(p.seuil_alerte) || 5;
            const delta = stock - reserve;
            let niveau = 'ok';
            if (delta < 0) niveau = 'rupture';
            else if (delta < seuil) niveau = 'faible';
            return { ...p, niveau, stock, reserve, delta };
          })
          .filter(p => p.niveau !== 'ok')
          .sort((a, b) => (a.niveau === 'rupture' ? -1 : 1) - (b.niveau === 'rupture' ? -1 : 1));
        setAlertesStock(alertes);
      }
      
      // Prochains Retraits (7 jours)
      try {
        const retraitsRes = await fetch(`${API_BASE}/reservations/stats/prochains-retraits`);
        if (retraitsRes.ok) {
          const retraitsData = await retraitsRes.json();
          const retraits = retraitsData.data || [];
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const retraitsFuturs = retraits.filter(r => {
            const dateRetrait = new Date(r.date);
            dateRetrait.setHours(0, 0, 0, 0);
            return dateRetrait >= today;
          });
          setProchainsRetraits(retraitsFuturs.slice(0, 7));
        }
      } catch (e) {
        console.log('Stats prochains retraits non disponibles');
      }
      
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Erreur chargement données dashboard:', error);
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // Refresh toutes les 2 min
    return () => clearInterval(interval);
  }, []);

  // Export Excel
  const handleExport = async (type) => {
    setExporting(true);
    try {
      let url = '';
      let filename = '';
      
      switch (type) {
        case 'reservations':
          url = `${API_BASE}/export/reservations-xlsx`;
          filename = 'reservations.xlsx';
          break;
        case 'produits':
          url = `${API_BASE}/export/produits-xlsx`;
          filename = 'produits.xlsx';
          break;
        case 'clients':
          url = `${API_BASE}/export/clients-xlsx`;
          filename = 'clients.xlsx';
          break;
        default:
          return;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(urlBlob);
      } else {
        alert('Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('Erreur export:', error);
      alert('Erreur: ' + error.message);
    }
    setExporting(false);
    setShowExportModal(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  
  const formatMontant = (montant) => parseFloat(montant || 0).toFixed(2) + '€';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
        <span className="ml-3 text-lg text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">📊 Vue Fermier - Coup d'œil</h2>
        <button 
          onClick={() => loadData()} 
          disabled={refreshing} 
          className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          KPIs - 4 CARTES
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Produits actifs */}
        <div className="bg-blue-50 p-5 rounded-xl border border-blue-200 shadow-sm">
          <div className="flex items-center">
            <Package className="h-10 w-10 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">Produits actifs</p>
              <p className="text-3xl font-bold text-blue-900">{dashboardData?.produitsActifs || dashboardData?.produits || 0}</p>
            </div>
          </div>
        </div>

        {/* Prêtes à retirer */}
        <div className="bg-green-50 p-5 rounded-xl border border-green-200 shadow-sm">
          <div className="flex items-center">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">Prêtes à retirer</p>
              <p className="text-3xl font-bold text-green-900">{dashboardData?.paniersPrets || 0}</p>
            </div>
          </div>
        </div>

        {/* À préparer */}
        <div className="bg-orange-50 p-5 rounded-xl border border-orange-200 shadow-sm">
          <div className="flex items-center">
            <Leaf className="h-10 w-10 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-orange-600">À préparer</p>
              <p className="text-3xl font-bold text-orange-900">{dashboardData?.aCueillir || 0}</p>
            </div>
          </div>
        </div>

        {/* CA Semaine */}
        <div className="bg-purple-50 p-5 rounded-xl border border-purple-200 shadow-sm">
          <div className="flex items-center">
            <TrendingUp className="h-10 w-10 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">CA Semaine</p>
              <p className="text-3xl font-bold text-purple-900">{formatMontant(dashboardData?.caSemaine || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          ALERTES STOCK - BIEN VISIBLE !
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          🚨 Alertes Stock
          {alertesStock.length > 0 && (
            <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full font-bold">
              {alertesStock.length} produit(s)
            </span>
          )}
        </h3>
        
        {alertesStock.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
            <p>✅ Aucune alerte stock - Tout va bien !</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Produit</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Stock</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Réservé</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Delta</th>
                  <th className="text-center py-2 px-3 text-sm font-medium text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {alertesStock.map((produit, idx) => (
                  <tr key={idx} className={`border-b ${produit.niveau === 'rupture' ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <td className="py-3 px-3 font-medium">{produit.designation || produit.nom}</td>
                    <td className="py-3 px-3 text-right">{produit.stock?.toFixed(1)} {produit.unite}</td>
                    <td className="py-3 px-3 text-right">{produit.reserve?.toFixed(1)} {produit.unite}</td>
                    <td className={`py-3 px-3 text-right font-bold ${produit.delta < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                      {produit.delta?.toFixed(1)}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {produit.niveau === 'rupture' ? (
                        <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">RUPTURE</span>
                      ) : (
                        <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">FAIBLE</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          PROCHAINS RETRAITS
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-500" />
          📅 Prochains Retraits (7 jours)
        </h3>
        
        {prochainsRetraits.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>Aucun retrait prévu dans les 7 prochains jours</p>
          </div>
        ) : (
          <div className="space-y-2">
            {prochainsRetraits.map((retrait, idx) => {
              const maxRetraits = Math.max(...prochainsRetraits.map(r => r.nb_reservations || 1));
              const barWidth = ((retrait.nb_reservations || 1) / maxRetraits) * 100;
              return (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium text-gray-700">{formatDate(retrait.date)}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-green-400 to-green-600 h-full rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(barWidth, 20)}%` }}
                    >
                      <span className="text-xs font-bold text-white">{retrait.nb_reservations} résa</span>
                    </div>
                  </div>
                  <div className="w-20 text-right text-sm font-bold text-green-700">
                    {formatMontant(retrait.montant_total)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          ACTIONS RAPIDES
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          🎯 Actions rapides
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Export XLS */}
          <button 
            onClick={() => setShowExportModal(true)} 
            className="bg-emerald-100 text-emerald-800 py-3 px-4 rounded-lg hover:bg-emerald-200 flex items-center justify-center gap-2 transition font-medium"
          >
            <FileSpreadsheet className="h-5 w-5" />
            Export XLS
          </button>
          
          {/* PDF Récap Mois */}
          <button 
            onClick={() => window.open(`${API_BASE}/etats-pdf/recap-mois?mois=${new Date().getMonth() + 1}&annee=${new Date().getFullYear()}`, '_blank')}
            className="bg-blue-100 text-blue-800 py-3 px-4 rounded-lg hover:bg-blue-200 flex items-center justify-center gap-2 transition font-medium"
          >
            <FileText className="h-5 w-5" />
            Récap Mois
          </button>
          
          {/* PDF Bilan Annuel */}
          <button 
            onClick={() => window.open(`${API_BASE}/etats-pdf/recap-annee?annee=${new Date().getFullYear()}`, '_blank')}
            className="bg-amber-100 text-amber-800 py-3 px-4 rounded-lg hover:bg-amber-200 flex items-center justify-center gap-2 transition font-medium"
          >
            <BarChart3 className="h-5 w-5" />
            Bilan Annuel
          </button>
          
          {/* PDF Stock Delta */}
          <button 
            onClick={() => window.open(`${API_BASE}/etats-pdf/stock-delta`, '_blank')}
            className="bg-purple-100 text-purple-800 py-3 px-4 rounded-lg hover:bg-purple-200 flex items-center justify-center gap-2 transition font-medium"
          >
            <Package className="h-5 w-5" />
            État Stock
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          MODAL EXPORT XLS
          ═══════════════════════════════════════════════════════════════════════════ */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
              Export Excel
            </h3>
            <p className="text-gray-600 mb-6">Choisissez les données à exporter :</p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExport('reservations')}
                disabled={exporting}
                className="w-full py-3 px-4 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50"
              >
                <Download className="h-5 w-5" />
                Réservations
              </button>
              <button
                onClick={() => handleExport('produits')}
                disabled={exporting}
                className="w-full py-3 px-4 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50"
              >
                <Download className="h-5 w-5" />
                Produits
              </button>
              <button
                onClick={() => handleExport('clients')}
                disabled={exporting}
                className="w-full py-3 px-4 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50"
              >
                <Download className="h-5 w-5" />
                Clients
              </button>
            </div>
            
            <button
              onClick={() => setShowExportModal(false)}
              className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardStats;
